import { useRef } from 'react';
import Script from 'next/script';
import { isRequired } from '@belong/common';
import { Button, Form, TextField } from '@belong/ui';
import clsx from 'clsx';
import { EmailField } from 'src/components/email-field/email-field';
import { useLogin } from 'src/features/auth/queries';
import { AUTH_STRINGS } from 'src/features/auth/strings';
import type { SignInFormSchema } from 'src/features/auth/types';
import { useStore } from 'src/store';
import { popModalSelector, pushModalSelector } from 'src/store/modal';
import css from './sign-in-form.module.css';

const recaptchaSiteKey = process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITEKEY || '';

type Props = {
  enforcedEmail?: string;
  defaultEmailValue?: string;
  onError?: (error: unknown) => void;
  onFinish?: (error?: unknown) => void;
  onStart?: () => void;
  onSuccess: () => void | Promise<void>;
  className?: string;
};

export function SignInForm({
  enforcedEmail,
  defaultEmailValue,
  onError = () => {},
  onFinish = () => {},
  onStart = () => {},
  onSuccess,
  className,
}: Props) {
  const hasError = useRef(false);
  const { login } = useLogin();
  const popModal = useStore(popModalSelector);
  const pushModal = useStore(pushModalSelector);

  const initialValues: SignInFormSchema = {
    email: enforcedEmail || defaultEmailValue || '',
    password: '',
  };

  async function handleSubmit(values: SignInFormSchema) {
    onStart();
    window.grecaptcha.ready(async () => {
      try {
        const recaptchaToken = await window.grecaptcha.execute(
          recaptchaSiteKey,
          { action: 'submit' },
        );
        await login({
          email: values.email,
          password: values.password as string,
          recaptchaToken,
        });
        await onSuccess();
        hasError.current = false;
      } catch (error) {
        onError(error);

        hasError.current = true;
      } finally {
        await onFinish(hasError.current);
      }
    });
  }

  function handleForgotPasswordClick() {
    pushModal({
      type: 'LOGIN',
      props: {
        currentScreen: 'forgot_password_screen',
        onDismiss: popModal,
        overlayClassName: 'z-fullcreen',
        successOverlayClassName: 'z-fullcreen',
      },
    });
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {() => {
        return (
          <div className={clsx('w-full', css.container, className)}>
            <div className={clsx('mb-2xl w-full')}>
              <div className="mb-lg">
                <EmailField
                  id="sign-in-email"
                  label={AUTH_STRINGS['email']}
                  name="email"
                  readOnly={!!enforcedEmail}
                />
              </div>
              <div className="mb-lg">
                <TextField
                  label={AUTH_STRINGS['password']}
                  name="password"
                  type="password"
                  validate={isRequired}
                />
              </div>
              <div className="mb-xl flex w-full justify-end">
                <Button onClick={handleForgotPasswordClick} variant="text">
                  {AUTH_STRINGS['sign.in.forgot.password']}
                </Button>
              </div>
            </div>
            <div className="w-full">
              <Form.SubmitButton size="fluid">
                {AUTH_STRINGS['sign.in']}
              </Form.SubmitButton>
            </div>
            <Script
              src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
            />
          </div>
        );
      }}
    </Form>
  );
}
